import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { createUser } from '@/services/userApi';

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const initialForm = {
  name: '',
  email: '',
  password: '',
  role: 'User',
  team: '',
  active: true,
};

export default function UserCreateDialog({ open, onClose, onCreated }: UserCreateDialogProps) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setErrorMessage('');
    }
  }, [open]);

  const updateField = (field: keyof typeof initialForm, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    if (form.name.trim().length < 1 || form.email.trim().length < 3 || form.password.length < 8) {
      setErrorMessage('Uzupełnij imię i nazwisko, login (min. 3 znaki) oraz hasło (min. 8 znaków).');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      await createUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        team: form.team.trim() || null,
        active: form.active,
      });
      onCreated();
      onClose();
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;

        if (status === 409) {
          setErrorMessage('Taki login jest już zajęty. Wybierz inny login.');
        } else if (status === 422 && Array.isArray(detail)) {
          const invalidFields = new Set<string>(
            detail
              .map((item: { loc?: unknown[] }) => item.loc?.at(-1))
              .filter((field: unknown): field is string => typeof field === 'string'),
          );
          const messages: string[] = [];
          if (invalidFields.has('name')) messages.push('podaj imię i nazwisko');
          if (invalidFields.has('email')) messages.push('login musi mieć co najmniej 3 znaki');
          if (invalidFields.has('password')) messages.push('hasło musi mieć od 8 do 128 znaków');
          setErrorMessage(messages.length ? `Sprawdź dane: ${messages.join(', ')}.` : 'Sprawdź poprawność wszystkich pól formularza.');
        } else if (typeof detail === 'string' && detail.trim()) {
          setErrorMessage(detail);
        } else {
          setErrorMessage('Nie udało się dodać użytkownika. Sprawdź połączenie i spróbuj ponownie.');
        }
      } else {
        setErrorMessage('Nie udało się dodać użytkownika. Sprawdź połączenie i spróbuj ponownie.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dodaj osobę</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField label="Imię i nazwisko" value={form.name} onChange={(event) => updateField('name', event.target.value)} required inputProps={{ maxLength: 255 }} fullWidth />
          <TextField label="Login" value={form.email} onChange={(event) => updateField('email', event.target.value)} helperText="Użyj tego loginu razem z hasłem na ekranie logowania. Minimum 3 znaki." required inputProps={{ minLength: 3, maxLength: 255 }} error={Boolean(form.email) && form.email.trim().length < 3} fullWidth />
          <TextField label="Hasło startowe" type="password" value={form.password} onChange={(event) => updateField('password', event.target.value)} helperText="Hasło musi mieć od 8 do 128 znaków." required inputProps={{ minLength: 8, maxLength: 128 }} error={Boolean(form.password) && (form.password.length < 8 || form.password.length > 128)} fullWidth />
          <FormControl fullWidth>
            <InputLabel id="user-role-label">Rola</InputLabel>
            <Select
              labelId="user-role-label"
              label="Rola"
              value={form.role}
              onChange={(event) => updateField('role', event.target.value)}
            >
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Zespół" value={form.team} onChange={(event) => updateField('team', event.target.value)} fullWidth />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(event) => updateField('active', event.target.checked)} />}
            label="Aktywny dostęp"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !form.name.trim() || form.email.trim().length < 3 || form.password.length < 8 || form.password.length > 128}>
          {submitting ? 'Zapisywanie...' : 'Dodaj osobę'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}